import React, { useEffect } from 'react';
import { Button, Empty, Spin, Typography } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';

import { MainTablePage } from '../../components/MainTablePage';

import { MatcherProcessingDetails } from './MatcherProcessingDetails';
import { MatchingPageLayout } from './matching.styles';
import { MatchingSource, Recommendation } from './matching.types';
import {
  counterpartUuid,
  matchingDetailPath,
  pageWideStates,
  recommendationEmptyMessage,
  sourceRecordPath,
} from './matchingPresentation';
import { MatchingReadError } from './MatchingReadError';
import { MatchingProcessingNotice, PageWideStateBanner } from './MatchingStatus';
import { RecommendationsSearchForm } from './RecommendationsSearchForm';
import { useMatchingScroll } from './useMatchingScroll';
import { useMatchingSource } from './useMatchingSource';
import { useRecommendations } from './useRecommendations';
import { useRecommendationSearch } from './useRecommendationSearch';
import { useRecommendationsTableColumnDefinitions } from './useRecommendationsTableColumnDefinitions';
import { useRecommendationsTableDataSource } from './useRecommendationsTableDataSource';

export const RecommendationsPage = ({ sourceType }: { sourceType: MatchingSource['type'] }) => {
  const { id = '' } = useParams();
  const source: MatchingSource = { type: sourceType, uuid: id };
  const context = useMatchingSource(source);
  const search = useRecommendationSearch(source);
  const query = useRecommendations(source, search.filters, search.from, search.max, !!id);
  const navigate = useNavigate();
  const rows = useRecommendationsTableDataSource(query.data);
  const pageWide = pageWideStates(rows);
  const columns = useRecommendationsTableColumnDefinitions(source, search.search, pageWide);
  const projects = sourceType === 'candidate';
  const { data } = query;
  useMatchingScroll(`${sourceType}:${id}:${search.search}`, !!data);
  // Filters or processing may shrink a result set while this page is open.
  useEffect(() => {
    if (data && search.from > 0 && search.from >= data.total && !query.isFetching) {
      search.setPage(Math.max(0, Math.ceil(data.total / search.max) - 1) * search.max);
    }
  }, [data, search, query.isFetching]);
  const heading = (
    <>
      <Link to={sourceRecordPath(source)} className="matching-back">
        <ArrowLeftOutlined /> Back to {sourceType}
      </Link>
      <div className="matching-header">
        <div>
          <h1>
            {projects ? 'Project' : 'Candidate'} matches for {context.name}
          </h1>
          <div className="matching-muted">Explore matches based on saved candidate and project data.</div>
        </div>
        <Button
          icon={<ReloadOutlined />}
          loading={query.isFetching && !query.isLoading}
          onClick={() => query.refetch()}
        >
          Refresh results
        </Button>
      </div>
      {context.isError && <MatchingReadError error={context.error} onRetry={() => context.refetch()} />}
    </>
  );
  return (
    <MatchingPageLayout>
      <MainTablePage<{ count: number }, Recommendation>
        mode="controlled"
        className="matching-table"
        header={heading}
        toolbar={
          <>
            <RecommendationsSearchForm source={source} search={search} />
            <MatchingReadError error={query.error} retained={!!data} onRetry={() => query.refetch()} />
            {data && <MatchingProcessingNotice {...data} />}
            <PageWideStateBanner rows={rows} pageWide={pageWide} />
            <div className="matching-results-heading">
              <div className="matching-results-count">
                <Typography.Text strong>
                  {data
                    ? `${data.total} ${data.total === 1 ? 'match' : 'matches'}`
                    : query.isLoading
                    ? 'Loading matches…'
                    : 'Matches'}
                </Typography.Text>
                {!!data?.quickRejectedCount && (
                  <span className="matching-muted">
                    {data.quickRejectedCount} more {data.quickRejectedCount === 1 ? 'was' : 'were'} rejected at the
                    initial check
                  </span>
                )}
              </div>
              <span className="matching-muted">Highest fit score first · Select a match to inspect it</span>
            </div>
          </>
        }
        tableProps={{
          columns,
          dataSource: rows,
          rowKey: counterpartUuid,
          loading: query.isLoading,
          rowClassName: 'matching-row',
          tableLayout: 'fixed',
          onRow: (row) => ({
            onClick: (event) => {
              if (
                (event.target as HTMLElement).closest('a,button,input,[role="button"]') ||
                window.getSelection()?.toString()
              )
                return;
              navigate(
                `${matchingDetailPath(source, counterpartUuid(row))}${search.search ? `?${search.search}` : ''}`
              );
            },
          }),
          pagination:
            data && data.total > 0
              ? {
                  current: Math.floor(search.from / search.max) + 1,
                  pageSize: search.max,
                  total: data.total,
                  showSizeChanger: true,
                  pageSizeOptions: [10, 20, 50, 100],
                  size: 'small',
                  onChange: (page, size) => search.setPage((page - 1) * size, size),
                  showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
                }
              : false,
          locale: {
            emptyText: (
              <div className="matching-empty">
                {query.isLoading ? (
                  <Spin />
                ) : query.isError && !data ? (
                  <span>No results loaded.</span>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={recommendationEmptyMessage(data, search.filtered, projects)}
                  >
                    {search.filtered && <Button onClick={search.clearFilters}>Clear filters</Button>}
                  </Empty>
                )}
              </div>
            ),
          },
        }}
        footer={data && <MatcherProcessingDetails data={data} />}
      />
    </MatchingPageLayout>
  );
};
